import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LAST_WRECK_CONTENT } from '../content';
import type { RuntimeEvent, WreckRuntimeController } from '../game';
import { ReducerSystemsBridge } from '../game/reducerSystemsBridge';
import { audioDirector, type SalvageSound } from '../audio/audio-director';
import { createInitialGameState, type GameEvent, type GameState } from '../systems';
import { useSettingsStore } from '../state/settings-store';
import { LastWreckScreen, type LastWreckActions } from '../ui';
import { gameStateToHud } from './hud-adapter';
import styles from './App.module.css';
import { VisualFixtureApp } from './VisualFixtureApp';

const PhaserGameCanvas = lazy(async () => {
  const module = await import('../game/PhaserGameCanvas');
  return { default: module.PhaserGameCanvas };
});

function BootStage() {
  return (
    <div className={styles.bootStage} role="status" aria-label="Initializing salvage drone">
      <img className={styles.bootDrone} src="/art/salvage-drone.png" alt="" />
      <div className={styles.bootCopy}>
        <span>Drone link // establishing</span>
        <strong>WASD to thrust · Mouse to aim</strong>
      </div>
    </div>
  );
}

function soundForGameEvent(event: GameEvent): SalvageSound | null {
  switch (event.type) {
    case 'SCAN_PULSE':
      return 'scan';
    case 'CUT_PROGRESS':
      return event.valid ? 'cut-valid' : 'cut-invalid';
    case 'CUTTER_OVERHEAT':
      return 'cut-invalid';
    case 'PRESSURE_EQUALIZED':
      return 'pressure';
    case 'GRAPPLE_ATTACHED':
      return 'grapple-attach';
    case 'GRAPPLE_TENSION':
      return event.warning ? 'grapple-creak' : null;
    case 'CABLE_BROKE':
      return 'grapple-break';
    case 'SALVAGE_BANKED':
      return 'deposit';
    case 'INSTABILITY_THRESHOLD':
      return event.threshold === 100 ? 'wreck-break' : 'event-warning';
    case 'RUN_ENDED':
      return event.result.ending === 'contract-failed' ? 'wreck-break' : 'success';
    default:
      return null;
  }
}

function soundForRuntimeEvent(event: RuntimeEvent): SalvageSound | null {
  switch (event.type) {
    case 'scan':
      return 'scan';
    case 'cut-contact':
      return event.valid ? null : 'cut-invalid';
    case 'grapple-attached':
      return 'grapple-attach';
    case 'grapple-released':
      return event.reason === 'overload' ? 'grapple-break' : 'ui';
    case 'salvage-deposited':
      return 'deposit';
    case 'hard-impact':
      return 'impact';
    case 'pressure-equalized':
      return 'pressure';
    case 'structural-warning':
      return event.threshold === 100 ? 'wreck-break' : 'event-warning';
    case 'power-changed':
      return 'ui';
    default:
      return null;
  }
}

function PlayableApp() {
  const [gameState, setGameState] = useState<GameState>(() => createInitialGameState());
  const [events, setEvents] = useState<readonly GameEvent[]>([]);
  const [mapVisible, setMapVisible] = useState(false);
  const [paused, setPaused] = useState(false);
  const controllerRef = useRef<WreckRuntimeController | null>(null);
  const recordedResult = useRef<string | null>(null);
  const settings = useSettingsStore((store) => store.settings);
  const bestScore = useSettingsStore((store) => store.bestScore);
  const completeTutorial = useSettingsStore((store) => store.completeTutorial);
  const recordRun = useSettingsStore((store) => store.recordRun);

  const [bridge] = useState(
    () =>
      new ReducerSystemsBridge({
        seed: 0x1a57b00b,
        onStateChange: setGameState,
        onGameEvents: (nextEvents, state) => {
          setEvents((current) => [...current, ...nextEvents].slice(-8));
          setGameState(state);
          for (const event of nextEvents) {
            const sound = soundForGameEvent(event);
            if (sound) audioDirector.play(sound);
          }
        },
      }),
  );

  useEffect(() => {
    audioDirector.configure({
      master: settings.masterVolume,
      music: settings.musicVolume,
      effects: settings.effectsVolume,
      muted: settings.muted,
    });
  }, [settings.effectsVolume, settings.masterVolume, settings.musicVolume, settings.muted]);

  useEffect(() => {
    audioDirector.setIntensity(
      gameState.instability.value,
      gameState.phase === 'emergency' || gameState.instability.value >= 85,
    );
  }, [gameState.instability.value, gameState.phase]);

  useEffect(() => {
    const unlock = () => void audioDirector.unlock();
    document.addEventListener('pointerdown', unlock, { once: true, capture: true });
    document.addEventListener('keydown', unlock, { once: true, capture: true });
    return () => {
      document.removeEventListener('pointerdown', unlock, { capture: true });
      document.removeEventListener('keydown', unlock, { capture: true });
    };
  }, []);

  useEffect(() => {
    if (gameState.tutorialStep === 'complete') completeTutorial();
    if (!gameState.result) return;
    const key = [gameState.seed, gameState.result.score, gameState.result.elapsedSeconds]
      .map(String)
      .join(':');
    if (recordedResult.current === key) return;
    recordedResult.current = key;
    recordRun(
      gameState.result.score,
      gameState.result.coreCondition,
      gameState.result.ending !== 'contract-failed',
    );
  }, [completeTutorial, gameState.result, gameState.seed, gameState.tutorialStep, recordRun]);

  useEffect(() => {
    const keyDown = (event: KeyboardEvent) => {
      if (event.key === 'Tab') {
        event.preventDefault();
        setMapVisible(true);
      }
    };
    const keyUp = (event: KeyboardEvent) => {
      if (event.key === 'Tab') setMapVisible(false);
    };
    window.addEventListener('keydown', keyDown);
    window.addEventListener('keyup', keyUp);
    return () => {
      window.removeEventListener('keydown', keyDown);
      window.removeEventListener('keyup', keyUp);
    };
  }, []);

  const handleRuntimeEvent = useCallback((event: RuntimeEvent) => {
    if (event.type === 'paused') setPaused(event.paused);
    const sound = soundForRuntimeEvent(event);
    if (sound) audioDirector.play(sound);
  }, []);

  const restart = useCallback(() => {
    bridge.reset();
    controllerRef.current?.restart();
    setGameState(bridge.gameState());
    setEvents([]);
    setMapVisible(false);
    setPaused(false);
    recordedResult.current = null;
  }, [bridge]);

  const contextAction = useCallback(() => {
    const current = bridge.gameState();
    if (current.tutorialStep === 'scan-hatch') {
      controllerRef.current?.selectTool('scanner');
      controllerRef.current?.pulseScanner();
      return;
    }
    if (current.tutorialStep === 'cut-hatch') {
      const controller = controllerRef.current;
      controller?.selectTool('cutter');
      controller?.cut('tutorial-hatch');
      return;
    }
    if (current.tutorialStep === 'grapple-cassette') {
      const controller = controllerRef.current;
      controller?.selectTool('grapple');
      audioDirector.play('grapple-fire');
      controller?.grapple('tool-cassette');
      return;
    }
    if (current.currentRoomId === 'power-control') {
      const bus = current.power.buses.A === 'off' ? 'A' : 'B';
      bridge.dispatchGameCommand({ type: 'SET_POWER', action: 'restore', bus });
      setGameState(bridge.gameState());
      return;
    }
    if (
      current.currentRoomId === LAST_WRECK_CONTENT.extractionRoomId &&
      current.salvage['navigation-core'].banked
    ) {
      bridge.dispatchGameCommand({ type: 'LEAVE_WRECK' });
      setGameState(bridge.gameState());
      return;
    }
    controllerRef.current?.pressContext();
  }, [bridge]);

  const actions = useMemo<LastWreckActions>(
    () => ({
      selectTool: (tool) => controllerRef.current?.selectTool(tool),
      useTool: (active) => controllerRef.current?.setUseTool(active),
      contextAction,
      move: (vector) => controllerRef.current?.setMove(vector),
      aim: (vector) => controllerRef.current?.setAim(vector),
      cancel: () => controllerRef.current?.cancelInteraction(),
      pause: (nextPaused) => {
        controllerRef.current?.setPaused(nextPaused);
        setPaused(nextPaused);
      },
      setMapVisible,
      restart,
      continueAfterResult: restart,
    }),
    [contextAction, restart],
  );

  const hud = gameStateToHud(gameState, events, { mapVisible, paused, bestScore });
  const gameCanvas = (
    <Suspense fallback={<BootStage />}>
      <PhaserGameCanvas
        systems={bridge}
        reducedMotion={settings.reducedMotion}
        onController={(controller) => {
          controllerRef.current = controller;
        }}
        onEvent={handleRuntimeEvent}
      />
    </Suspense>
  );

  return <LastWreckScreen state={hud} actions={actions} gameCanvas={gameCanvas} />;
}

export function App() {
  if (import.meta.env.DEV) {
    const fixture = new URLSearchParams(window.location.search).get('visual');
    if (fixture !== null) return <VisualFixtureApp fixture={fixture} />;
  }
  return <PlayableApp />;
}
